import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-vertical-scroll-container',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: './vertical-scroll-container.component.html',
  styleUrl: './vertical-scroll-container.component.scss'
})
export class VerticalScrollContainerComponent implements AfterContentInit, OnDestroy {
  @ViewChild('scrollContainer') container?: ElementRef;
  content?: HTMLElement;
  upButton?: HTMLButtonElement;
  downButton?: HTMLButtonElement;
  scrollAmount: number = 100;
  scrollInterval: any = null;
  scrollSpeed: number = 50;
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;

  constructor() { }

  ngAfterContentInit() {
    // Initial setup after content is initialized
    setTimeout(() => {
      this.initializeElements();
      this.setupObservers();
      this.updateButtonStates();
    }, 0);
  }
 
  ngOnDestroy() {
    this.cleanup();
  }

  private initializeElements() {
    if (this.container) {
      this.content = this.container.nativeElement.querySelector('.scroll-content');
      this.upButton = this.container.nativeElement.querySelector('.scroll-up-button');
      this.downButton = this.container.nativeElement.querySelector('.scroll-down-button');
    }
  }

  private setupObservers() {
    if (this.content) {
      // Create mutation observer to watch for content changes
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(() => {
          requestAnimationFrame(() => this.updateButtonStates());
        });
      });

      // Watch for all changes in the content area
      this.mutationObserver.observe(this.content, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });

      // Create resize observer to watch for size changes
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => this.updateButtonStates());
      });

      // Watch for size changes in the content
      this.resizeObserver.observe(this.content);
    }
  }

  private cleanup() {
    
    // Disconnect observers
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();

    // Clear any remaining scroll interval
    this.stopScrolling();
  }
 

  upButtonDown(event: any) {
    this.startScrolling('up');
  }

  upButtonUp(event: any) {
    this.stopScrolling();
  }

  upButtonLeave(event: any) {
    this.stopScrolling();
  }

  downButtonDown(event: any) {
    this.startScrolling('down');
  }

  downButtonUp(event: any) {
    this.stopScrolling();
  }

  downButtonLeave(event:any) {
    this.stopScrolling();
  }

  onScroll(event:any) {
    this.updateButtonStates()
  }

  startScrolling(direction: string) {
    if (this.scrollInterval) return;

    const scroll = () => {
      if (direction === 'up') {
        this.content?.scrollBy({
          top: -this.scrollAmount,
          behavior: 'smooth'
        });
      } else {
        this.content?.scrollBy({
          top: this.scrollAmount,
          behavior: 'smooth'
        });
      }
      this.updateButtonStates();
    };

    // Initial scroll
    scroll();
    // Continue scrolling while held
    this.scrollInterval = setInterval(scroll, this.scrollSpeed);
  }

  stopScrolling() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  updateButtonStates() {
    if (this.content) {
      const isAtTop = this.content.scrollTop <= 0;
      const isAtBottom = this.content.scrollTop + this.content.clientHeight >= this.content.scrollHeight;

      // Update button states and classes for styling
      if (this.upButton) {
        this.upButton.disabled = isAtTop;
        this.upButton.classList.toggle('disabled', isAtTop);
      }
      if (this.downButton) {
        this.downButton.disabled = isAtBottom;
        this.downButton.classList.toggle('disabled', isAtBottom);
      }
    }
  }
}
